type KmlImportErrorDetails = {
  featureName?: string
  featureNames?: string[]
  geometryType?: string
  minimumAreaHa?: number
  thresholdPercentage?: number
  count?: number
}

// Returning null rather than rendering a placeholder is deliberate: a message naming
// `undefined` is worse for the operator than the caller's existing generic fallback.
type MessageBuilder = (details: KmlImportErrorDetails) => string | null

const withFeatureName =
  (build: (featureName: string) => string): MessageBuilder =>
  ({ featureName }) =>
    featureName ? build(featureName) : null

const messages: Record<string, MessageBuilder> = {
  IMPORT_GEOMETRY_TOO_SMALL: ({ featureName, minimumAreaHa }) =>
    featureName && minimumAreaHa != null
      ? `El lote "${featureName}" es menor al mínimo permitido (${minimumAreaHa} ha). Verifique su geometría en el archivo.`
      : null,

  IMPORT_GEOMETRY_OUTSIDE_PERIMETER: withFeatureName(
    featureName => `El lote "${featureName}" está fuera del perímetro del establecimiento.`,
  ),

  IMPORT_GEOMETRY_MOSTLY_OUTSIDE_PERIMETER: ({ featureName, thresholdPercentage }) =>
    featureName && thresholdPercentage != null
      ? `El lote "${featureName}" excede el perímetro del establecimiento en más del ${thresholdPercentage}% de su superficie.`
      : null,

  IMPORT_GEOMETRY_TYPE_INVALID: ({ featureName, geometryType }) =>
    featureName && geometryType
      ? `El lote "${featureName}" tiene una geometría no soportada (${geometryType}). Solo se admiten polígonos.`
      : null,

  IMPORT_FEATURE_NAME_DUPLICATE: withFeatureName(
    featureName =>
      `Hay más de un lote llamado "${featureName}" en el archivo. Los nombres deben ser únicos.`,
  ),

  IMPORT_FEATURE_NAME_INVALID: withFeatureName(
    featureName => `El nombre de lote "${featureName}" no es válido.`,
  ),

  IMPORT_FEATURE_NAME_MISSING: ({ count }) => {
    if (!count) return null
    return count === 1
      ? '1 lote del archivo no tiene nombre. Todos los lotes deben tener uno.'
      : `${count} lotes del archivo no tienen nombre. Todos los lotes deben tener uno.`
  },

  IMPORT_FEATURE_GEOMETRY_MISSING: ({ count }) => {
    if (!count) return null
    return count === 1
      ? '1 lote del archivo no tiene geometría.'
      : `${count} lotes del archivo no tienen geometría.`
  },

  IMPORT_NAMES_ALREADY_EXIST: ({ featureNames }) => {
    if (!featureNames?.length) return null
    return featureNames.length === 1
      ? `Ya existe un lote con este nombre en el loteo: ${featureNames[0]}. Renómbrelo antes de importar.`
      : `Ya existen lotes con estos nombres en el loteo: ${featureNames.join(', ')}. Renómbrelos antes de importar.`
  },

  IMPORT_NO_AREA_AFTER_CLIP: withFeatureName(
    featureName =>
      `El lote "${featureName}" no conserva superficie después del recorte contra los lotes existentes.`,
  ),

  IMPORT_NOT_ENABLED: () =>
    'La importación parcial no está habilitada para el programa de este establecimiento.',

  IMPORT_FILE_MISSING: () =>
    'No se recibió ningún archivo. Seleccione un KML e intente nuevamente.',
}

export const getKmlImportErrorMessage = (error: unknown): string | null => {
  const response = (
    error as { response?: { errorCode?: string; errorDetails?: KmlImportErrorDetails } }
  )?.response
  const build = response?.errorCode ? messages[response.errorCode] : undefined
  return build ? build(response?.errorDetails ?? {}) : null
}
